/**
 * A founder's people and their setup progress, and the way to invite a teammate (board onboarding-r2, answer 6). One
 * set of pieces for the founder's dashboard and the admin founder page, composed from the library only.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { ACCESS_CHOICES, accessWords, inviteProblem, personProgress, type Reader } from '../../domain/people'
import type { Access, PersonView } from '../../domain/schemas'
import { ApiError, type Api } from '../../infra/api'
import { visibleInterval } from '../../infra/visibleInterval'
import { useLoad } from '../useLoad'
import { Button, Chip, DataTable, EmptyState, ErrorNote, Inline, Input, Light, LightStrip, Link, Segmented, Stack, Text } from '../../ui'

const ROLE: Record<PersonView['role'], string> = { founder: 'Founder', teammate: 'Teammate', viewer: 'Viewer', admin: 'cpg.computer' }

/** Shared or own: the choice and its one line. */
export function AccessChoice({ value, onChange, admin = false }: { value: Access; onChange: (v: Access) => void; admin?: boolean }) {
  const picked = ACCESS_CHOICES.find((c) => c.value === value)!
  return (
    <Stack gap="xs">
      <Segmented label="Their computer account" size="sm" value={value} onChange={onChange} options={ACCESS_CHOICES.map((c) => ({ value: c.value, label: admin ? c.adminLabel : c.label }))} />
      <Text size="sm" tone="muted">{admin ? picked.adminLine : picked.line}</Text>
    </Stack>
  )
}

/**
 * Everyone listed, with their account and progress. `admin` adds the Linux user and a link to their Add person run;
 * `me` marks the reader's own row.
 */
export function PeopleTable({ people, reader, me }: { people: PersonView[]; reader: Reader; me?: string }) {
  const admin = reader === 'admin'
  return (
    <DataTable
      label="People"
      framed={false}
      rows={people}
      rowKey={(p) => p.id}
      attention={(p) => p.run?.status === 'failed'}
      empty={<EmptyState title="Nobody listed yet.">{admin ? 'Add a person lists them and sets up their access on the box.' : 'Invite a teammate to build with you.'}</EmptyState>}
      columns={[
        {
          key: 'person',
          label: 'Person',
          narrow: 'main',
          render: (p) => (
            <Stack gap="none">
              <Text weight="head">{`${p.name ?? p.email}${p.id === me ? ' (you)' : ''}`}</Text>
              {p.name && <Text mono size="xs" tone="muted">{p.email}</Text>}
            </Stack>
          ),
        },
        { key: 'role', label: 'Role', narrow: 'end', render: (p) => <Chip tone="mute">{ROLE[p.role]}</Chip> },
        {
          key: 'access',
          label: 'Computer',
          render: (p) =>
            p.access ? (
              <Text size="sm">{`${accessWords({ role: p.role, access: p.access }, reader, p.id === me)}${admin && p.linuxUser ? ` · ${p.linuxUser}` : ''}`}</Text>
            ) : (
              <Text size="sm" tone="muted">{p.role === 'viewer' ? 'no account' : 'not yet'}</Text>
            ),
        },
        {
          key: 'progress',
          label: 'Setup',
          narrowLabel: false,
          render: (p) => {
            const pr = personProgress(p, { admin })
            return (
              <Stack gap="xs">
                <Inline gap="xs">
                  <Light state={pr.light} size="sm" />
                  <Text size="sm">{pr.words}</Text>
                  {admin && p.run?.id && <Link to={`/runs/${p.run.id}`}>run</Link>}
                </Inline>
                <LightStrip lights={pr.stages.map((s) => ({ key: s.key, label: s.label, state: s.light }))} />
              </Stack>
            )
          },
        },
      ]}
    />
  )
}

/** The founder's "Invite a teammate": email, an optional name, shared or own. Starts an Add person run. */
export function InviteForm({ people, invite, onInvited, onCancel }: { people: PersonView[]; invite: (input: { email: string; name?: string; ownUser: boolean }) => Promise<unknown>; onInvited: () => void; onCancel: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [access, setAccess] = useState<Access>('shared')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string>()

  async function submit(e: FormEvent) {
    e.preventDefault()
    const problem = inviteProblem(email, people)
    if (problem) return setError(problem)
    setSending(true)
    setError(undefined)
    try {
      await invite({ email: email.trim().toLowerCase(), ...(name.trim() ? { name: name.trim() } : {}), ownUser: access === 'own' })
      onInvited()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setSending(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="sm">
        <Input type="email" aria-label="Their email" placeholder="Their email" value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="off" autoComplete="off" required autoFocus />
        <Input aria-label="Their name (optional)" placeholder="Their name (optional)" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        <AccessChoice value={access} onChange={setAccess} />
        <Text size="sm" tone="muted">They sign in at app.cpg.computer with this email, then pair a device and pick their agent.</Text>
        {error && <ErrorNote title="Not invited yet." reason={error} />}
        <Inline gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="ink" size="sm" type="submit" disabled={sending || !email.includes('@')}>
            {sending ? 'Inviting…' : 'Invite'}
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}

/** While someone's Add person run is going, the list asks again this often. */
const POLL_MS = 4000

/**
 * The founder's team: the list, and "Invite a teammate" once their computer exists (founders only; the API says so in
 * `canInvite`). Admins pass the founder and see the same list with run links.
 */
export function TeamPanel({ api, me, founderId, reader }: { api: Pick<Api, 'people' | 'invite'>; me?: string; founderId?: string; reader: Reader }) {
  const admin = reader === 'admin'
  const list = useLoad(() => api.people(founderId), [api, founderId])
  const [inviting, setInviting] = useState(false)
  /** The email just invited: its run starts in the background, so they may not be listed on the first read. */
  const [awaiting, setAwaiting] = useState<string | null>(null)
  const settled = (p: PersonView) => p.run?.status === 'done' || p.run?.status === 'failed'
  const arrived = awaiting !== null && (list.data?.people.some((p) => p.email === awaiting && settled(p)) ?? false)
  const busy = (awaiting !== null && !arrived) || (list.data?.people.some((p) => p.run?.status === 'running' || p.run?.status === 'pending') ?? false)
  useEffect(() => (busy ? visibleInterval(list.reload, POLL_MS) : undefined), [busy, list.reload])
  useEffect(() => void (arrived && setAwaiting(null)), [arrived])

  if (list.error && !list.data) return <ErrorNote title="Your people didn't load." reason={list.error.message} onRetry={list.reload} />
  if (!list.data) return <Text size="sm" tone="muted">One moment.</Text>
  return (
    <Stack gap="sm">
      <PeopleTable people={list.data.people} reader={reader} {...(me ? { me } : {})} />
      {inviting ? (
        <InviteForm
          people={list.data.people}
          invite={async (input) => {
            await api.invite(input, founderId)
            setAwaiting(input.email)
          }}
          onInvited={() => (setInviting(false), list.reload())}
          onCancel={() => setInviting(false)}
        />
      ) : list.data.canInvite ? (
        <Inline gap="sm">
          <Button size="sm" icon="plus" onClick={() => setInviting(true)}>
            Invite a teammate
          </Button>
        </Inline>
      ) : (
        !admin && <Text size="sm" tone="muted">Your founder invites teammates from their dashboard.</Text>
      )}
    </Stack>
  )
}
